import { ellipsisFilter } from "app/filters/ellipsisFilter";
import { prettifyCategoryFilter } from "app/filters/prettifyCategoryFilter";
import CountryService from "app/services/CountryService";
import { checkResultWithFullResponse } from "app/services/fetchService";

import type { ISiteInfoItem, SiteInfoResponse } from "./types";

export const getUrlDomain = (urlString: string): string => {
    urlString = urlString.toLowerCase()?.trim()?.replace("http://", "")?.replace("https://", "")?.split("?")?.[0];
    const domain = urlString?.match(/^(.*:\/\/)?(www\.)?([.\-_a-zA-Z0-9]*)(\/?).*$/);

    return domain?.[3];
};

export const isValidUrl = (urlString: string): boolean => {
    const domain = getUrlDomain(urlString);

    if (!domain) {
        return false;
    }

    const lastIndexOfDot = domain.lastIndexOf(".");
    return lastIndexOfDot > 0 && domain.slice(lastIndexOfDot)?.length > 2;
};

export const getFaviconUrl = (domain: string, size = 16) =>
    `https://www.google.com/s2/favicons?domain=${domain}&sz=${size}`;

export const isResultResponse = (
    response: SiteInfoResponse | null,
): response is { result: Record<string, ISiteInfoItem> } => {
    return typeof response === "object" && response !== null && "result" in response;
};

export const getSiteDataFromResponse = (
    response: SiteInfoResponse | null,
    siteKey: string,
): ISiteInfoItem | undefined => {
    if (!response) {
        return undefined;
    }
    if (isResultResponse(response)) {
        return response.result[siteKey];
    }
    return response[siteKey];
};

export const transformSiteInfoResponse = (response: SiteInfoResponse | null): SiteInfoResponse | null => {
    if (!response) {
        return response;
    }

    const data: Record<string, ISiteInfoItem> = checkResultWithFullResponse(response)
        ? (response as { result: Record<string, ISiteInfoItem> }).result
        : (response as Record<string, ISiteInfoItem>);
    Object.entries<ISiteInfoItem>(data).forEach(([key, val]) => {
        if (val.description) {
            val.description = val.description.replace(/\uFFFD/g, "");
        }
        val.el_description = ellipsisFilter()(val.description || "", 196);
        val.category = prettifyCategoryFilter()(val.category || "");
        if (val.highestTrafficCountry && typeof val.highestTrafficCountry === "number") {
            const countryId = val.highestTrafficCountry;
            const country = CountryService.countriesById[countryId];
            if (country) {
                // Transform number to country object - this is intentional mutation for backward compatibility
                (val as { highestTrafficCountry: unknown }).highestTrafficCountry = country;
            }
        }
        val.displayName = val.IsVGroup ? key.substr(1) : key;
    });

    return response;
};
